import { useEffect, useState } from "react";
import { Check, EyeOff, Trash2, BadgeCheck, Reply } from "lucide-react";
import api, { apiErr, mediaUrl } from "@/lib/api";
import { Stars } from "@/components/store/Stars";
import { toast } from "sonner";

const TABS = [["pending", "Pending"], ["published", "Published"], ["hidden", "Hidden"], ["", "All"]];

export default function AdminReviews() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ reviews: [], counts: {} });
  const [replies, setReplies] = useState({});

  const load = () => api.get("/admin/reviews", { params: { status: tab } }).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, [tab]);

  const setStatus = async (r, status) => {
    try { await api.put(`/admin/reviews/${r.id}`, { status }); toast.success(`Review ${status}.`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveReply = async (r) => {
    try { await api.put(`/admin/reviews/${r.id}`, { reply: replies[r.id] ?? r.reply ?? "" }); toast.success("Reply saved."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (r) => { if (!window.confirm("Delete this review permanently?")) return; await api.delete(`/admin/reviews/${r.id}`); load(); };

  return (
    <div data-testid="admin-reviews">
      <div className="mb-8"><h1 className="font-heading text-3xl">Review Moderation</h1><p className="font-body text-sm text-ink/50 mt-1">Approve, hide, reply to and remove customer reviews.</p></div>

      <div className="flex gap-1 mb-6">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`reviews-tab-${k || "all"}`} className={`font-body uppercase tracking-wide2 text-[0.6rem] px-4 py-2 border transition-colors ${tab === k ? "bg-ink text-linen border-ink" : "border-ink/20 text-ink/60 hover:border-ink"}`}>
            {l}{k && data.counts?.[k] != null ? ` (${data.counts[k]})` : ""}
          </button>
        ))}
      </div>

      {data.reviews.length === 0 && <p className="font-body text-sm text-ink/40 py-12 text-center" data-testid="reviews-empty">No reviews here.</p>}

      <div className="space-y-4">
        {data.reviews.map((r) => (
          <div key={r.id} className="adm-card" data-testid={`admin-review-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-body text-[0.6rem] uppercase tracking-wide2 text-ink/40">{r.product_title}</p>
                <div className="flex items-center gap-2 mt-1"><Stars value={r.rating} /><span className="font-heading text-base">{r.title}</span></div>
                <p className="font-body text-sm text-ink/70 mt-1">{r.body}</p>
                <p className="font-body text-[0.65rem] uppercase tracking-wide2 text-ink/50 mt-2 flex items-center gap-1">— {r.author_name}{r.verified && <span className="inline-flex items-center gap-1 text-ink"><BadgeCheck size={12} /> Verified</span>} · {(r.created_at || "").slice(0, 10)}</p>
                {r.photos?.length > 0 && <div className="flex gap-2 mt-2">{r.photos.map((p, i) => <img key={i} src={mediaUrl(p)} alt="" className="w-14 h-14 object-cover border hairline" />)}</div>}
              </div>
              <span className={`px-2 py-0.5 text-[0.6rem] uppercase tracking-wide2 ${r.status === "published" ? "bg-ink text-linen" : r.status === "hidden" ? "bg-warm text-ink/50" : "bg-[#c9a24b] text-white"}`}>{r.status}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              {r.status !== "published" && <button onClick={() => setStatus(r, "published")} className="adm-btn" data-testid={`review-approve-${r.id}`}><Check size={13} /> Approve</button>}
              {r.status !== "hidden" && <button onClick={() => setStatus(r, "hidden")} className="adm-btn-outline" data-testid={`review-hide-${r.id}`}><EyeOff size={13} /> Hide</button>}
              <button onClick={() => del(r)} className="adm-btn-outline" data-testid={`review-delete-${r.id}`}><Trash2 size={13} /> Delete</button>
            </div>

            <div className="mt-4 flex gap-2 items-start">
              <Reply size={16} className="text-ink/40 mt-2" />
              <input className="input-luxe flex-1" placeholder="Reply publicly to this reviewer…" defaultValue={r.reply || ""} onChange={(e) => setReplies((s) => ({ ...s, [r.id]: e.target.value }))} data-testid={`review-reply-input-${r.id}`} />
              <button onClick={() => saveReply(r)} className="adm-btn-outline" data-testid={`review-reply-save-${r.id}`}>Save Reply</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
